/**
 * §7.4 畫面 × 權限對照的路由表。
 *
 * 全部畫面都已實作——先前放占位頁的 NotBuiltYet 元件連同檔案一起移除了。
 * 留著一個沒有人用的占位元件，下一個人會以為還有畫面沒做。
 *
 * 導覽項目由伺服器決定（§7.1），但**路徑仍然要能直接輸入**——
 * §7.3 明講「仍可透過 /station/OP2 直接輸入網址進入其他站（權限允許的話），
 * 但導覽上不出現」。所以這裡是完整的路由表，不是只有 nav 裡的那幾個。
 */

import { useAuth } from '../auth/AuthContext.js'
import { PasswordChangeScreen } from '../auth/PasswordChangeScreen.js'
import { Alert, Card } from '../components/ui.js'
import { Link, useRoute } from '../router.js'
import { WorkBench } from './WorkBench.js'
import { Inventory } from './Inventory.js'
import { PanelDetail } from './PanelDetail.js'
import { StationBalance } from './StationBalance.js'
import { StationOverview } from './StationOverview.js'
import { AuditLog } from './AuditLog.js'
import { ChainDashboard } from './ChainDashboard.js'
import { VerifyPending } from './VerifyPending.js'
import { Receiving } from './Receiving.js'
import { IssueOrderScreen } from './IssueOrder.js'
import { StationWork } from './StationWork.js'
import { Settings } from './Settings.js'
import { Shipment } from './Shipment.js'
import { StockCount } from './StockCount.js'
import { IdPool } from './IdPool.js'
import { AdminAccounts } from './admin/AdminAccounts.js'
import { AdminSites } from './admin/AdminSites.js'
import { AdminSystem } from './admin/AdminSystem.js'
import { AdminAuth } from './admin/AdminAuth.js'
import { AdminRoles } from './admin/AdminRoles.js'
import { AdminSessions } from './admin/AdminSessions.js'
import { AdminAuthLog } from './admin/AdminAuthLog.js'

export function Screen() {
  const { parts } = useRoute()
  const { can, canAny } = useAuth()

  const guard = (need: string[], el: JSX.Element, mode: 'all' | 'any' = 'all') =>
    (mode === 'all' ? can(...need) : canAny(...need)) ? el : <Forbidden need={need} />

  switch (parts[0]) {
    case undefined:
      return <Redirecting />

    // ── 作業 ────────────────────────────────────────────────────────────
    case 'work':
      return guard(['workorder.open'], <WorkBench />)
    case 'receiving':
      return guard(['receiving.register'], <Receiving />)
    case 'issue':
      return guard(['workorder.open', 'issue.post'], <IssueOrderScreen />)
    case 'station':
      return guard(['receipt.post'], <StationWork stationId={parts[1] ?? 'OP1'} />)
    case 'shipment':
      return guard(['shipment.post'], <Shipment />)
    case 'count':
      return guard(['stockcount.record'], <StockCount />)

    // ── 查詢 ────────────────────────────────────────────────────────────
    case 'inventory':
      return guard(['inventory.read'], <Inventory />)
    case 'panel':
      return guard(['panel.read'], <PanelDetail panelId={decodeURIComponent(parts[1] ?? '')} />)
    case 'balance':
      return guard(['balance.read'], <StationBalance workOrderId={decodeURIComponent(parts[1] ?? '')} />)
    case 'overview':
      return guard(['balance.read'], <StationOverview />)
    case 'verify':
      return guard(['weigh.verify'], <VerifyPending />)
    case 'dashboard':
      return guard(['report.read'], <ChainDashboard />)
    case 'audit':
      return guard(['audit.read'], <AuditLog />)

    // ── 設定 ────────────────────────────────────────────────────────────
    case 'settings':
      // 只有 settings.model.create 的人（操作員、覆核者、檢視者）也要進得來——
      // 頁籤本身會依權限過濾，他們只看得到「型號基準」
      return guard(['settings.read', 'settings.model.create'], <Settings />, 'any')
    case 'idpool':
      // 現場也要看得到預提了哪些號碼（§7.4 的號碼池頁），所以 receiving.register 也放行
      return guard(['idpool.generate', 'idpool.reserve', 'receiving.register'], <IdPool />, 'any')

    // ── 系統 ────────────────────────────────────────────────────────────
    case 'admin':
      switch (parts[1]) {
        case 'accounts': return guard(['account.read'], <AdminAccounts />)
        case 'sites': return guard(['site.read'], <AdminSites newSite={parts[2] === 'new'} />)
        case 'system': return guard(['system.backup'], <AdminSystem />)
        case 'roles': return guard(['role.write'], <AdminRoles />)
        // 兩個權限任一個就進得來：system.smtp 管寄信、system.auth 管登入方式開關。
        // 內建角色兩個都有，但自訂角色可能只給一個
        case 'auth': return guard(['system.smtp', 'system.auth'], <AdminAuth />, 'any')
        case 'sessions': return guard(['session.manage'], <AdminSessions />)
        case 'auth-log': return guard(['authaudit.read'], <AdminAuthLog />)
        default: return <NotFound />
      }

    case 'me':
      return <PasswordChangeScreen />

    default:
      return <NotFound />
  }
}

function Redirecting() {
  return <p className="text-sm text-ink-500">正在前往您的工作畫面…</p>
}

/**
 * §6.4 前端這一半：權限不足時不渲染畫面。
 * 訊息要說清楚缺什麼權限、該找誰——不要只寫「無權限」。
 */
function Forbidden({ need }: { need: string[] }) {
  const { me } = useAuth()
  return (
    <Card title="沒有這個畫面的權限">
      <p className="text-sm text-ink-700">
        這個畫面需要 {need.map((n) => <code key={n} className="mx-0.5 rounded bg-ink-100 px-1">{n}</code>)} 權限。
      </p>
      <p className="mt-2 text-sm text-ink-600">
        您目前的角色是 <strong>{me?.roles.join('、') || '（無）'}</strong>。
        若這是您的職責範圍，請聯絡系統管理者調整角色。
      </p>
      <p className="mt-3 text-sm">
        <Link to={me?.home ?? '/'} className="text-sky-700 underline">回到我的工作畫面</Link>
      </p>
    </Card>
  )
}

function NotFound() {
  const { me } = useAuth()
  const { parts } = useRoute()
  return (
    <Alert tone="warn" title="找不到這個畫面">
      <p>路徑 <code>/{parts.join('/')}</code> 不存在。可能是網址打錯，或這個功能還沒做。</p>
      <p className="mt-2">
        <Link to={me?.home ?? '/'} className="underline">回到我的工作畫面</Link>
      </p>
    </Alert>
  )
}
